import React from "react";
import { Icon, Stack, Text, DocumentCard } from "@fluentui/react";
import { ICatalystCustomerDetails } from "../../../../interfaces/Catalyst.interfaces";

export interface CatalystScoreCardProps {
  data?: ICatalystCustomerDetails;
}

interface ScoreCardItem {
  title: string;
  value?: string;
  aiGenerated?: boolean;
}

const CARD_STYLE = {
  root: {
    width: "100%",
    maxWidth: "100%",
    height: "100%",
    padding: 16,
    borderRadius: 8,
    boxShadow: "0 1.6px 3.6px rgba(0,0,0,0.13), 0 0.3px 0.9px rgba(0,0,0,0.1)",
    minHeight: 140,
    boxSizing: "border-box" as const,
  },
};

const DEFAULT_PLACEHOLDER = "Please select a customer from the filters above to view detailed insights.";

const ScoreCardCell: React.FC<{ item: ScoreCardItem }> = ({ item }) => {
  const isDefault = item.value === DEFAULT_PLACEHOLDER;
  return (
  <DocumentCard styles={CARD_STYLE}>
    <Stack tokens={{ childrenGap: 8 }} styles={{ root: { height: "100%" } }}>
      <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: "#323130" } }}>
        {item.title}
      </Text>
      <Text
        variant="medium"
        styles={{ root: { color: "#323130", whiteSpace: "pre-wrap", overflow: "auto", flex: 1, fontStyle: isDefault ? "italic" : "normal" } }}
      >
        {item.value || "—"}
      </Text>      {item.aiGenerated && (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }} styles={{ root: { marginTop: 4 } }}>
          <Icon iconName="ShieldSolid" styles={{ root: { fontSize: 14, color: "#0078D4" } }} />
          <Text variant="smallPlus" styles={{ root: { color: "#605E5C", fontStyle: "italic" } }}>
            AI-generated content may be incorrect
          </Text>
        </Stack>
      )}    </Stack>
  </DocumentCard>
);
};

export const CatalystScoreCard: React.FC<CatalystScoreCardProps> = ({ data }) => {
  const items: ScoreCardItem[] = data
    ? [
        { title: "AI Summary on Composite Score", value: data.aiSummaryonCompositeScore, aiGenerated: true },
        { title: "Customer Sentiment Drivers and Insights", value: data.customerSentimentDriversandInsights },
        { title: "Customers Key Objectives Success Measures", value: data.customersKeyObjectivesSuccessMeasures },
        { title: "Protection Insights", value: data.protectionInsights },
        { title: "Overall Catalyst Engagement", value: data.overallCatalystEngagement },
        { title: "Emerging Risk", value: data.emergingRisk, aiGenerated: true },
        { title: "Support Status", value: data.supportStatus },
        { title: "Weekly Tweet", value: data.weeklyTweet },
      ]
    : [
        { title: "AI Summary on Composite Score", value: DEFAULT_PLACEHOLDER, aiGenerated: true },
        { title: "Customer Sentiment Drivers and Insights", value: DEFAULT_PLACEHOLDER },
        { title: "Customers Key Objectives Success Measures", value: DEFAULT_PLACEHOLDER },
        { title: "Protection Insights", value: DEFAULT_PLACEHOLDER },
        { title: "Overall Catalyst Engagement", value: DEFAULT_PLACEHOLDER },
        { title: "Emerging Risk", value: DEFAULT_PLACEHOLDER, aiGenerated: true },
        { title: "Support Status", value: DEFAULT_PLACEHOLDER },
        { title: "Weekly Tweet", value: DEFAULT_PLACEHOLDER },
      ];

  return (
    <Stack tokens={{ childrenGap: 16 }} styles={{ root: { overflow: "hidden" } }}>
      <Text variant="xLarge" styles={{ root: { fontWeight: 600, color: "#0078D4" } }}>
        Catalyst Score Card
      </Text>
      <Stack tokens={{ childrenGap: 16 }}>
        {[0, 2, 4, 6].map((startIdx) => (
          <div key={startIdx} style={{ display: "flex", gap: 16, alignItems: "stretch" }}>
            <div style={{ flex: 1, minWidth: 0, display: "flex" }}>
              <ScoreCardCell item={items[startIdx]} />
            </div>
            <div style={{ flex: 1, minWidth: 0, display: "flex" }}>
              <ScoreCardCell item={items[startIdx + 1]} />
            </div>
          </div>
        ))}
      </Stack>
    </Stack>
  );
};
